import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePageContent } from "@/i18n/pageContent";
import { localizeCategory } from "@/i18n/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsArchive() {
  const c = usePageContent("newsArchive");
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles`, { params: { lang: i18n.language } });
        if (!cancelled) setPosts(data || []);
      } catch (e) {
        console.error("Failed to load articles", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        title={<>{c.titleA} <span className="italic">{c.titleB}</span>.</>}
        subtitle={c.subtitle}
      />

      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading && (
            <div className="text-center text-[#475569] py-10">{t("common.loading")}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {posts.map((n) => (
              <Link
                key={n.slug}
                to={`/articles/${n.slug}`}
                className="group card-lift block reveal"
              >
                <div className="zoom-wrap aspect-[4/3] w-full bg-[#fafaf7] relative">
                  <img
                    src={n.image_url}
                    alt={n.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#1a365d] font-medium">
                    {localizeCategory(n.category, i18n.language)}
                  </span>
                </div>
                <div className="pt-5">
                  <span className="text-[11px] tracking-[0.28em] uppercase text-[#475569] font-body">
                    {n.date}
                  </span>
                  <h3 className="font-display text-lg md:text-xl text-[#1a365d] mt-2.5 leading-[1.25] group-hover:text-[#d4af37] transition-colors">
                    {n.title}
                  </h3>
                  {n.subtitle && (
                    <p className="font-display italic text-[#475569] text-sm mt-2 leading-snug">
                      {n.subtitle}
                    </p>
                  )}
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-[#1a365d] group-hover:text-[#d4af37] transition-colors">
                    Read <ArrowUpRight size={12} strokeWidth={2} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
